import {
  IKeyObjective,
  IKeyObjectiveTag,
} from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { IJbpSettings, SobActualRequiredQuarter } from 'legacy/lut/features/partner360/jbpSettings'
import { IDashboardSetting } from 'legacy/lut/features/partner360/jbpSettings/models/dashboardSetting'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'
import { StBusinessUnitOption } from 'legacy/lut/features/partner360/jbpSettings/models/stBusinessUnitOption'
import { StRoleOption } from 'legacy/lut/features/partner360/jbpSettings/models/stRoleOption'

import { parseRawSetting } from '../helpers/parseRawSetting'
import { IJbpSettingsApi } from '../models/jbpSettingsApi'

import { mapAccessDeniedSettings } from './mapAccessDeniedSettings'
import { mapMaintenanceSettings } from './mapMaintenanceSettings'
import { mapMappings } from './mapMappings'
import { mapOfferingsList } from './mapOfferingsList'
import { mapStatisticsSettings } from './mapStatisticsSettings'

type RawSobActualRequiredQuarter = {
  FQuarter?: number | string | null
  SOBState?: number | string | null
}

type RawKIBusinessPriority = {
  UID?: string | null
  FYear?: number | string | null
  Name?: string | null
  Markets?: string | null
  Offerings?: string | null
  Status?: string | null
  Enabled?: boolean | number | string | null
  Created?: string
}

const parseSeparatedSetting = (value?: string | null): string[] =>
  (value ?? '')
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean)

const normalizeEnabledSetting = (value?: boolean | number | string | null): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return value === 1
  }

  const normalizedValue = value?.trim().toLowerCase()

  if (['true', '1', 'yes', 'active', 'enabled'].includes(normalizedValue ?? '')) {
    return true
  }

  if (['false', '0', 'no', 'inactive', 'disabled'].includes(normalizedValue ?? '')) {
    return false
  }

  return undefined
}

const normalizeKIBusinessPriorities = (value?: string): IKIBusinessPriority[] => {
  const parsed = parseRawSetting<RawKIBusinessPriority[]>({
    defaultValue: [],
    setting: value,
  })

  return (Array.isArray(parsed) ? parsed : [])
    .map((item) => ({
      uid: item?.UID?.trim().toLowerCase() ?? '',
      fYear: Number(item?.FYear),
      name: item?.Name?.trim() ?? '',
      markets: parseSeparatedSetting(item?.Markets),
      offerings: parseSeparatedSetting(item?.Offerings),
      status: item?.Status?.trim() || undefined,
      enabled: normalizeEnabledSetting(item?.Enabled),
      created: item?.Created,
    }))
    .filter((item) => Number.isFinite(item.fYear) && Boolean(item.uid) && Boolean(item.name))
}

const normalizeSobActualRequiredQuarters = (value: string): SobActualRequiredQuarter[] => {
  const parsed = parseRawSetting<RawSobActualRequiredQuarter[]>({
    defaultValue: [],
    setting: value,
  })
  const normalizedByQuarter = new Map<number, SobActualRequiredQuarter>()

  parsed.forEach((item) => {
    const fQuarter = Number(item?.FQuarter)
    const sobState = Number(item?.SOBState)

    if (![1, 2, 3, 4].includes(fQuarter) || ![-1, 0, 1].includes(sobState)) {
      return
    }

    normalizedByQuarter.set(fQuarter, {
      fQuarter: fQuarter as SobActualRequiredQuarter['fQuarter'],
      sobState: sobState as SobActualRequiredQuarter['sobState'],
    })
  })

  const normalized = [1, 2, 3, 4]
    .map((quarter) => normalizedByQuarter.get(quarter))
    .filter((item): item is SobActualRequiredQuarter => Boolean(item))

  return normalized.length === 4 ? normalized : []
}

export const mapApiToJbpSettings = (data: IJbpSettingsApi): IJbpSettings => {
  return {
    entryId: data.EntryId,
    accessDeniedSettings: mapAccessDeniedSettings(data.AccessDeniedSettings),
    commitmentCompletenessThreshold: data.CommitmentCompletenessThreshold,
    compensationDaysRange: data.CompensationDaysRange,
    currentFQuarter: data.CurrentFQuarter,
    currentFYear: data.CurrentFYear,
    distributorExtendedPlanning: data.DistributorExtendedPlanning,
    exportHRActualsMappings: mapMappings(data.ExportHRActualsMappings),
    exportMIFReport: mapMappings(data.ExportMIFReport),
    exportMPSSummaryReportV2: mapMappings(data.ExportMPSSummaryReportV2),
    exportMasterDealerReport: mapMappings(data.ExportMasterDealerReport),
    exportTopPartnerSummaryReport: mapMappings(data.ExportTopPartnerSummaryReport),
    exportSalesTeamRequests: mapMappings(data.ExportSalesTeamRequests),
    fYear: data.FYear,
    fQuarter: data.FQuarter,
    fDateActuals: data.FDateActuals,
    fYearActuals: data.FYearActuals,
    fQuarterActuals: data.FQuarterActuals,
    fQuarterActualsPercentage: data.FQuarterActualsPercentage,
    finSobPlanQuarterlyChecks: data.FinSobPlanQuarterlyChecks ?? 0,
    qbrGuideLinks: JSON.parse(data.QBRGuideLinks),
    fYears: data.FYears.split(',').map((it) => parseInt(it)),
    generatePDFOnBackend: data.GeneratePDFOnBackend,
    kiBusinessPriorities: normalizeKIBusinessPriorities(data.KIBusinessPriorities),
    keyObjectives: parseRawSetting<IKeyObjective[]>({
      setting: data.KeyObjectives,
      defaultValue: [],
    }),
    keyObjectiveTags: parseRawSetting<IKeyObjectiveTag[]>({
      setting: data.KeyObjectiveTags,
      defaultValue: [],
    }),
    mdfToolsLink: data.MDFToolsLink,
    mscsProviderPLs: data.MSCSProviderPLs,
    maintenanceSettings: mapMaintenanceSettings(data.MaintenanceSettings),
    pbmPerformanceActualsReport: mapMappings(data.PBMPerformanceActualsReport),
    pbmPerformanceInitiativesReport: mapMappings(data.PBMPerformanceInitiativesReport),
    pbmToPartnerCheckEnabled: data.PBMToPartnerCheckEnabled,
    psLifecyclePLs: data.PSLifecyclePLs,
    psManageabilityPLs: data.PSManageabilityPLs,
    psSecurityPLs: data.PSSecurityPLs,
    amplifyDataInsightsTraining: data.AmplifyDataInsightsTraining,
    amplifyDataInsightsHandbook: data.AmplifyDataInsightsHandbook,
    statisticsSettings: mapStatisticsSettings(data.StatisticsSettings),
    quickStartGuideLink: data.QuickStartGuideLink,
    mdStatuses: data.MDStatuses.split(',').map((it) => it),
    mdEPStatuses: data.MDEPStatuses.split(',').map((it) => it),
    uamGuidePDFLink: data.UAMGuidePDFLink,
    reportsPartnerTypes: data.ReportsPartnerTypes ? data.ReportsPartnerTypes.split(',') : [],
    reportsFYears: data.ReportsFYears ? data.ReportsFYears.split(',').map((it) => Number(it)) : [],
    dashboardsSettings: parseRawSetting<IDashboardSetting[]>({
      setting: data.DashboardsSettings,
      defaultValue: [],
    }),

    stBusinessUnits: parseRawSetting<StBusinessUnitOption[]>({
      setting: data.STBusinessUnits,
      defaultValue: [],
    }),
    stRoles: parseRawSetting<StRoleOption[]>({
      setting: data.STRoles,
      defaultValue: [],
    }),
    sobActualRequiredQuarters: normalizeSobActualRequiredQuarters(data.SOBActualRequiredQuarters),
    offeringsList: mapOfferingsList(data.OfferingsList),

    isH2Review: data.IsH2Review ?? 0,

    fYearELT: data.FYearELT || 0,
    fQuarterELT: data.FQuarterELT || 1,
  }
}
